import { FC } from "react";
import { Link } from "react-router-dom";

import { ReactComponent as AntennaIcon } from "assets/images/common/antenna.svg";
import { ReactComponent as BellIcon } from "assets/images/common/bell.svg";
import { ReactComponent as SettingsIcon } from "assets/images/common/settings.svg";

import Card from "components/Card";

import { UserRole } from "entities/common";

import PageTemplate from "modules/common/PageTemplate";

import { useAppSelector } from "store/hooks";

import { canManageIaqLimits } from "utils/helpers";
import { PAGES } from "utils/links";

import styles from "./style.module.scss";

type SettingItem = {
  key: string;
  title: string;
  description: string;
  icon: React.ReactNode;
  link?: string;
};

const SettingsPage: FC = () => {
  const { user } = useAppSelector((state) => state.auth);
  const isAdministrator = user?.role === UserRole.ADMINISTRATOR;
  const canSeeIaqLimitSettings = canManageIaqLimits(user);

  const settingItems: SettingItem[] = [
    {
      key: "notifications",
      title: "Notifications",
      description: "Configure notification channels and alert preferences",
      icon: <BellIcon />,
      link: PAGES.SETTINGS_NOTIFICATIONS,
    },
    ...(isAdministrator
      ? [
          {
            key: "gateway",
            title: "Gateway management",
            description: "Monitor and manage IoT gateways across your system",
            icon: <AntennaIcon />,
            link: PAGES.SETTINGS_GATEWAYS_LIST,
          },
        ]
      : []),
    ...(canSeeIaqLimitSettings
      ? [
          {
            key: "iaq-limit-settings",
            title: "IAQ limit settings",
            description:
              "Configure threshold and comfort range limits for all IAQ metrics. Applied globally across the entire system.",
            icon: <SettingsIcon />,
            link: PAGES.SETTINGS_IAQ_LIMIT_SETTINGS,
          },
        ]
      : []),
  ];

  return (
    <PageTemplate
      title="Settings"
      description="Manage your WOLKIS platform configuration and preferences"
      className={styles.page}
    >
      <Card className={styles.card}>
        <ul className={styles.list}>
          {settingItems.map((item) => {
            const content = (
              <>
                <div className={styles.itemContent}>
                  <h3 className={styles.itemTitle}>{item.title}</h3>
                  <p className={styles.itemDescription}>{item.description}</p>
                </div>

                <span className={styles.itemIcon}>{item.icon}</span>
              </>
            );

            return (
              <li key={item.key} className={styles.item}>
                {item.link ? (
                  <Link to={item.link} className={styles.itemLink}>
                    {content}
                  </Link>
                ) : (
                  content
                )}
              </li>
            );
          })}
        </ul>

        <div className={styles.overview}>
          <h4 className={styles.overviewTitle}>Settings Overview</h4>
          <p className={styles.overviewDescription}>
            Configure all aspects of your WOLKIS platform from this central settings hub. Each category provides
            specialized configuration options for different areas of the system.
          </p>
          <span className={styles.overviewBar} />
        </div>
      </Card>
    </PageTemplate>
  );
};

export default SettingsPage;
